import {
	AudioMutedOutlined,
	AudioOutlined,
	CaretDownOutlined,
	CaretUpOutlined,
	CloseCircleOutlined,
	DownOutlined,
	NumberOutlined,
	PhoneFilled,
	PhoneOutlined,
	SoundOutlined,
} from "@ant-design/icons"
import { useAsyncEffect, useMount } from "ahooks"
import { Avatar } from "components/Avatar/Avatar"
import { Button, type ButtonProps } from "components/Button/Button"
import { Dropdown } from "components/Dropdown/Dropdown"
import { useNotification } from "components/Notification/Notification"
import { Popover } from "components/Popover/Popover"
import { Select } from "components/Select/Select"
import { Space } from "components/Space/Space"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useBeforeUnload } from "hooks/useBeforeUnloadd"
import { useDisclosure } from "hooks/useDisclosure"
import { useTimer } from "hooks/useTimer"
import { CallStatus, Participant } from "interface/communications"
import { Patient } from "interface/patientType"
import { useRealtimeSubscription } from "modules/realtime/useRealtimeSubscription"
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react"
import { createPortal } from "react-dom"
import { sortByFullName } from "utils/patient"
import { useTwilio } from "./useTwilio"

export const Voice = Object.assign({}, { CallToAction, Controls, useControls })

type State = {
	isOpen: boolean
	participant?: Partial<Participant>
	callStatus?: CallStatus
	open: (participant: State["participant"]) => void
	close: VoidFunction
	setCallStatus: (callStatus: CallStatus) => void
}

const voiceInitialState: State = {
	isOpen: false,
	close: () => null,
	open: () => null,
	setCallStatus: () => {},
}

const VoiceContext = createContext<State>(voiceInitialState)

function CallToAction({
	show = true,
	forceParticipantId,
	patient,
	type = "primary",
	size,
	className,
}: Pick<ButtonProps, "type" | "size" | "className"> & {
	show?: boolean
	forceParticipantId?: string
	patient: Pick<
		Patient,
		"id" | "fullName" | "phoneNumber" | "alternateContacts"
	>
}) {
	const { open, isOpen } = useContext(VoiceContext)
	const participants = useMemo(() => {
		if (!patient) {
			return []
		}
		const participants: Partial<Participant>[] = []

		if (!!patient.phoneNumber?.value) {
			participants.push({
				id: patient.id,
				fullName: patient.fullName,
				displayName: patient.fullName,
			})
		}

		if (patient.alternateContacts) {
			participants.push(
				...patient.alternateContacts
					.filter(({ isActive }) => isActive)
					.toSorted(sortByFullName)
					.map(({ _id: id, fullName, relationship }) => ({
						id,
						fullName,
						displayName: `${fullName} (${relationship})`,
					})),
			)
		}
		return participants
	}, [patient])

	if (
		!show ||
		participants.length === 0 ||
		(!!forceParticipantId &&
			participants.every(({ id }) => id !== forceParticipantId))
	) {
		return null
	}

	if (!!forceParticipantId || participants.length === 1) {
		return (
			<Button
				type={type}
				size={size}
				className={className}
				icon={<PhoneFilled aria-hidden />}
				disabled={isOpen}
				onClick={() => {
					if (forceParticipantId) {
						open(participants.find(({ id }) => id === forceParticipantId))
					} else {
						open(participants[0])
					}
				}}
			>
				Call
			</Button>
		)
	}

	return (
		<Space.Compact size={size} className={className}>
			<Button
				type={type}
				className={className}
				disabled={isOpen}
				icon={<PhoneFilled aria-hidden />}
				onClick={() => open(participants[0]!)}
			>
				Call
			</Button>
			<Dropdown
				trigger={["click"]}
				placement="bottomRight"
				menu={{
					"aria-label": "Available contacts to call",
					items: participants.map(({ id, displayName }) => ({
						key: id!,
						label: displayName,
					})),
					onClick: ({ key }) => open(participants.find(({ id }) => id === key)),
				}}
			>
				<Button
					type={type}
					disabled={isOpen}
					icon={<DownOutlined aria-hidden />}
					aria-label="Select a contact to call"
				/>
			</Dropdown>
		</Space.Compact>
	)
}

function Controls({ children }: { children: ReactNode }) {
	const [state, setState] = useState<State>(voiceInitialState)
	const open = useCallback(
		(participant: State["participant"]) =>
			setState((previousState) => ({
				...previousState,
				participant,
				isOpen: true,
			})),
		[],
	)
	const close = useCallback(() => setState(voiceInitialState), [])
	const { formattedDuration, startTimer, resetTimer } = useTimer()
	const setCallStatus = useCallback(
		(callStatus: State["callStatus"]) => {
			if (callStatus === CallStatus.INITIATED) {
				startTimer()
			}
			setState((previousState) => ({
				...previousState,
				callStatus,
			}))
		},
		[startTimer],
	)
	const onHangUp = useCallback(() => {
		resetTimer()
		close()
	}, [close, resetTimer])
	const value = useMemo(
		() => ({
			...state,
			open,
			close,
			setCallStatus,
		}),
		[close, open, setCallStatus, state],
	)
	const { input, inputs, output, outputs, setCurrentDevice } = useAudioDevices(
		state.isOpen,
	)
	const {
		callOpen,
		muted,
		hangup,
		mute,
		sendDigit,
		setInput,
		setOutput,
		intitializeDevice,
	} = useTwilio({
		To: state.participant?.id,
		onHangUp,
	})
	const settings = useDisclosure()
	const disableActions = !callOpen || state.callStatus !== CallStatus.ACCEPTED
	const leaveButtonTitle = "Leave call"

	useBeforeUnload(state.isOpen)

	useEffect(() => {
		if (state.isOpen) {
			intitializeDevice()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.isOpen])

	useRealtimeSubscription("voice_call_status_update", (data) => {
		if (data.callStatus) {
			setCallStatus(data.callStatus)
		}
	})

	return (
		<VoiceContext value={value}>
			{children}

			{state.isOpen &&
				createPortal(
					<div
						role="region"
						aria-label="Voice controls"
						className="@container/voice flex flex-col justify-between gap-3 border-t border-t-grey-20 bg-white p-5"
					>
						<div className="flex flex-wrap items-center justify-between gap-2">
							<Avatar className="size-11 min-h-11 min-w-11 text-2xl">
								{state.participant?.fullName}
							</Avatar>
							<div className="mr-auto flex flex-col gap-1">
								<p className="text-lg font-semibold">
									{state.participant?.displayName}
								</p>
								<div className="flex items-center gap-4 text-grey-60">
									<time className="text-navy-100" aria-label="Call time">
										{formattedDuration}
									</time>
									{!!state.callStatus && (
										<span className="text-navy-60">
											Call is {state.callStatus}
										</span>
									)}
								</div>
							</div>
							<div className="ml-auto flex gap-2">
								<AudioSettings
									open={settings.isOpen}
									disabled={disableActions}
									muted={muted}
									onToggleSettings={settings.toggle}
									onToggleMute={mute}
								/>

								<Keypad onClickDigit={sendDigit} disabled={disableActions} />

								<Tooltip title={leaveButtonTitle}>
									<Button
										shape="circle"
										size="large"
										type="primary"
										danger
										icon={<PhoneOutlined aria-hidden />}
										disabled={!callOpen}
										aria-label={leaveButtonTitle}
										onClick={hangup}
									/>
								</Tooltip>
							</div>
						</div>
						{settings.isOpen && (
							<div className="flex w-full justify-end gap-2 @max-xl:flex-wrap">
								<Select
									value={input}
									aria-label="Modify audio input"
									className="min-w-0 @max-xl:w-full"
									prefix={<AudioOutlined aria-hidden />}
									options={inputs.map(({ deviceId, label }) => ({
										label,
										value: deviceId,
									}))}
									onChange={async (deviceId) => {
										setCurrentDevice("audioinput", deviceId)
										await setInput(deviceId)
									}}
								/>
								<Select
									value={output}
									aria-label="Modify audio output"
									className="min-w-0 @max-xl:w-full"
									prefix={<SoundOutlined aria-hidden />}
									options={outputs.map(({ deviceId, label }) => ({
										label,
										value: deviceId,
									}))}
									onChange={async (deviceId) => {
										setCurrentDevice("audiooutput", deviceId)
										await setOutput(deviceId)
									}}
								/>
							</div>
						)}
					</div>,
					document.querySelector("#layout-root") ?? document.body,
				)}
		</VoiceContext>
	)
}

function Keypad({
	disabled,
	onClickDigit,
	...props
}: ButtonProps & {
	onClickDigit: (digit: string) => void
}) {
	const keypadButtonTitle = "Access keypad"
	const [digitHistory, setDigitHistory] = useState("")
	const handleClickDigit = (digit: string) => {
		onClickDigit(digit)
		setDigitHistory((prev) => `${prev}${digit}`)
	}
	return (
		<Popover
			trigger="click"
			classNames={{
				title: "max-w-32 min-w-auto",
			}}
			title={
				<div className="flex max-w-full min-w-0 items-center justify-between gap-1">
					<div className="flex truncate font-bold [direction:rtl]">
						{digitHistory}
					</div>
					<CloseCircleOutlined
						className="size-6 place-content-center"
						aria-label="Clear"
						onClick={() => setDigitHistory("")}
					/>
				</div>
			}
			content={
				<div className="grid grid-cols-3 place-items-center gap-1">
					{AVAILABLE_DIGITS.map((digit) => (
						<Button
							key={digit}
							shape="circle"
							size="large"
							icon={digit}
							aria-label={digit}
							onClick={() => handleClickDigit(digit)}
						/>
					))}
				</div>
			}
		>
			<Tooltip title={keypadButtonTitle}>
				<Button
					{...props}
					shape="circle"
					size="large"
					aria-label={keypadButtonTitle}
					icon={<NumberOutlined aria-hidden />}
					disabled={disabled}
				/>
			</Tooltip>
		</Popover>
	)
}

function AudioSettings({
	open,
	muted,
	disabled,
	onToggleMute,
	onToggleSettings,
}: {
	open: boolean
	muted: boolean
	disabled: boolean
	onToggleMute: () => void
	onToggleSettings: () => void
}) {
	const audioButtonTitle = "Audio settings"
	const muteButtonTitle = muted
		? "Turn on the microphone"
		: "Turn off the microphone"

	return (
		<Space.Compact>
			<Tooltip title={audioButtonTitle}>
				<Button
					shape="circle"
					size="large"
					disabled={disabled}
					danger={muted}
					aria-label={audioButtonTitle}
					icon={
						open ? (
							<CaretUpOutlined aria-hidden />
						) : (
							<CaretDownOutlined aria-hidden />
						)
					}
					onClick={onToggleSettings}
				/>
			</Tooltip>
			<Tooltip title={muteButtonTitle}>
				<Button
					shape="circle"
					size="large"
					disabled={disabled}
					aria-label={muteButtonTitle}
					danger={muted}
					icon={
						muted ? (
							<AudioMutedOutlined aria-hidden />
						) : (
							<AudioOutlined aria-hidden />
						)
					}
					onClick={onToggleMute}
				/>
			</Tooltip>
		</Space.Compact>
	)
}

function useAudioDevices(isOpen: boolean) {
	const { notify } = useNotification()
	const [state, setState] = useState<{
		input: string
		inputs: MediaDeviceInfo[]
		output: string
		outputs: MediaDeviceInfo[]
	}>({ input: "", inputs: [], output: "", outputs: [] })
	const setCurrentDevice = useCallback(
		(kind: MediaDeviceKind, deviceId: string) =>
			setState((prev) => ({
				...prev,
				[kind === "audioinput" ? "input" : "output"]: deviceId,
			})),
		[],
	)
	const loadAudioDevices = useCallback(async () => {
		const devices = await navigator.mediaDevices.enumerateDevices()
		const defaultInput = devices.find(
			({ kind, deviceId }) => kind === "audioinput" && deviceId === "default",
		)
		const inputs = devices.filter(
			({ kind, deviceId }) => kind === "audioinput" && deviceId !== "default",
		)
		const input = inputs.find(
			({ groupId }) => groupId === defaultInput?.groupId,
		)
		const defaultOutput = devices.find(
			({ kind, deviceId }) => kind === "audiooutput" && deviceId === "default",
		)
		const outputs = devices.filter(
			({ kind, deviceId }) => kind === "audiooutput" && deviceId !== "default",
		)
		const output = outputs.find(
			({ groupId }) => groupId === defaultOutput?.groupId,
		)
		setState({
			input: input?.deviceId ?? inputs[0].deviceId,
			inputs,
			output: output?.deviceId ?? outputs[0].deviceId,
			outputs,
		})
	}, [])
	const askAudioPermission = useCallback(async () => {
		try {
			await navigator.mediaDevices.getUserMedia({ audio: true })
			await loadAudioDevices()
		} catch (error) {
			console.error(error)
		}
	}, [loadAudioDevices])

	useMount(async () => {
		navigator.mediaDevices.addEventListener("devicechange", askAudioPermission)

		return () => {
			navigator.mediaDevices.removeEventListener(
				"devicechange",
				askAudioPermission,
			)
		}
	})

	useAsyncEffect(async () => {
		if (isOpen) {
			await askAudioPermission()
		}
	}, [isOpen])

	useAsyncEffect(async () => {
		const key = "microphone-permission-notification"
		const permission = await navigator.permissions.query({
			name: "microphone",
		})

		if (isOpen && permission.state === "denied") {
			notify.error({
				key,
				duration: 0,
				message: "We need access to your microphone to place calls",
				description: (
					<div className="flex flex-col">
						<p>
							It looks like the <b>microphone</b> was blocked earlier. To fix
							this:
						</p>
						<ul className="list-inside list-disc">
							<li>
								Click the <b>lock icon</b> (next to the URL).
							</li>
							<li>
								Set Microphone to <b>Allow</b>.
							</li>
							<li>Refresh the page.</li>
						</ul>
					</div>
				),
			})
		} else {
			// notify.destroy(key)
		}
	}, [notify, isOpen])

	return { ...state, setCurrentDevice }
}

function useControls() {
	const { isOpen, setCallStatus } = useContext(VoiceContext)
	return { isOpen, setCallStatus }
}

const AVAILABLE_DIGITS = [
	"1",
	"2",
	"3",
	"4",
	"5",
	"6",
	"7",
	"8",
	"9",
	"*",
	"0",
	"#",
]
